import React, { useRef, useState } from "react";
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from "lucide-react";
import { cn } from "~/lib/utils";
import NotificationDetails from "~/components/NotificationDetails";

type Props = {
  children: React.ReactNode;
};

export type NotificationData = {
  kind?: "success" | "error" | "warning" | "info";
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  caption?: React.ReactNode;
  className?: string;
  details?: string;
};

const kindConfig = {
  success: { Icon: CircleCheck, border: "border-l-success-600", icon: "text-success-600" },
  error: { Icon: CircleAlert, border: "border-l-destructive", icon: "text-destructive" },
  warning: { Icon: TriangleAlert, border: "border-l-warning-600", icon: "text-warning-600" },
  info: { Icon: Info, border: "border-l-primary-600", icon: "text-primary-600" },
} as const;

export const ToastNotificationContext = React.createContext<{
  notificationData: NotificationData;
  notificationVisible: boolean;
  setNotificationData: (value: NotificationData) => void;
  setNotificationVisible: (value: boolean) => void;
  showNotificationWithTimeout: () => void;
}>({
  notificationData: { kind: "success" },
  notificationVisible: false,
  setNotificationData: () => {},
  setNotificationVisible: () => {},
  showNotificationWithTimeout: () => {},
});

const ToastNotificationWrapper = ({ children }: Props) => {
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState<NotificationData>({ kind: "success" });
  // Mirror the latest data so showWithTimeout can decide how long to keep the toast up.
  const dataRef = useRef(data);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setNotificationData = (value: NotificationData) => {
    dataRef.current = value;
    setData(value);
  };

  const showWithTimeout = () => {
    setVisible(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // Toasts with details take longer to read (and to expand/copy) than plain ones.
    const timeout = dataRef.current?.details ? 10000 : 5000;
    timeoutRef.current = setTimeout(() => setVisible(false), timeout);
  };

  const { details } = data;
  const cfg = kindConfig[data.kind ?? "success"];
  const Icon = cfg.Icon;

  return (
    <ToastNotificationContext.Provider
      value={{
        notificationData: data,
        notificationVisible: visible,
        setNotificationData,
        setNotificationVisible: setVisible,
        showNotificationWithTimeout: showWithTimeout,
      }}
    >
      {visible && (
        <div
          className={cn(
            "fixed right-4 top-[80px] z-[10000] min-w-[25rem] max-w-[28rem] rounded-md border border-l-4 bg-white p-4 shadow-[0px_0px_2px_0px_#00000017,0px_0px_8px_0px_#00000012]",
            cfg.border,
            data.className
          )}
        >
          <div className="flex gap-3">
            <Icon className={cn("mt-0.5 size-5 shrink-0", cfg.icon)} />
            <div className="min-w-0 flex-1">
              {!!data.title && <div className="text-sm font-semibold">{data.title}</div>}
              {!!data.subtitle && <div className="text-sm [color:var(--text-02)]">{data.subtitle}</div>}
              {!!data.caption && <div className="text-xs [color:var(--text-02)]">{data.caption}</div>}
              {details && <NotificationDetails details={details} />}
            </div>
            <button
              type="button"
              aria-label="Close notification"
              className="shrink-0 text-muted-foreground hover:text-foreground"
              onClick={() => setVisible(false)}
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
      )}
      {children}
    </ToastNotificationContext.Provider>
  );
};

export default ToastNotificationWrapper;
